import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { MessageCircle, Loader2, ShieldCheck, Truck } from "lucide-react";
import { buildWhatsAppLink, submitLead } from "../lib/api";

export default function QuoteDialog({ open, onOpenChange, product }) {
  const [quantity, setQuantity] = useState("");
  const [mobile, setMobile] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setQuantity("");
    setMobile("");
    setDescription("");
  };

  if (!product) return null;

  const selectedWeight = product.selectedVariants?.weight || "";
  const selectedColor = product.selectedVariants?.color || "";
  const selectedVariantText = [
    selectedWeight ? `Weight: ${selectedWeight}` : "",
    selectedColor ? `Colour: ${selectedColor}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!quantity || !mobile) {
      toast.error("Quantity & mobile are required");
      return;
    }

    if (!/^[0-9+\-\s]{7,15}$/.test(mobile.trim())) {
      toast.error("Please enter a valid mobile number");
      return;
    }

    const savedDescription = [
      selectedWeight ? `Weight: ${selectedWeight}` : "",
      selectedColor ? `Colour: ${selectedColor}` : "",
      description.trim() ? `Requirement: ${description.trim()}` : "",
    ]
      .filter(Boolean)
      .join(" | ");

    const link = buildWhatsAppLink({
      productName: product.name,
      productSlug: product.slug,
      quantity,
      mobile: mobile.trim(),
      description: description.trim(),
      weight: selectedWeight,
      color: selectedColor,
    });

    setLoading(true);

    try {
      await submitLead({
        product_slug: product.slug,
        product_name: product.name,
        quantity: String(quantity),
        mobile: mobile.trim(),
        description: savedDescription,
        source: "quote_form",
      });

      window.location.href = link;
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Lead save error:", error);
      toast.error("Lead could not be saved, opening WhatsApp.");
      window.location.href = link;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] rounded-lg border border-[#BFD3C6] p-0 overflow-hidden" data-testid="quote-dialog">
        <div className="bg-[#075B3A] text-white px-6 py-4">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/75">Quick Quote</span>
        </div>

        <div className="p-6">
          <DialogHeader className="text-left mb-4">
            <DialogTitle className="font-display text-2xl font-black text-[#0F172A]">{product.name}</DialogTitle>
            <DialogDescription className="text-sm text-[#5F6B66]">
              {selectedVariantText ? (
                <>Selected: <span className="font-bold text-[#075B3A]">{selectedVariantText}</span></>
              ) : (
                <>
                  {product.weight && <>Weight: <span className="font-bold text-[#0F172A]">{product.weight}</span></>}
                  {product.weight && product.material && " · "}
                  {product.material && <>Material: {product.material}</>}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.14em] text-[#647169]" htmlFor="qty">Quantity (pairs) *</Label>
              <Input id="qty" type="text" inputMode="numeric" placeholder="e.g. 500" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="mt-1.5 h-11 focus-visible:ring-[#075B3A]" required />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.14em] text-[#647169]" htmlFor="mob">Mobile Number *</Label>
              <Input id="mob" type="tel" placeholder="98765 43210" value={mobile} onChange={(e) => setMobile(e.target.value)} className="mt-1.5 h-11 focus-visible:ring-[#075B3A]" required />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.14em] text-[#647169]" htmlFor="desc">Requirement Details</Label>
              <Textarea id="desc" placeholder="e.g. Monthly requirement, dispatch city, application..." value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="mt-1.5 focus-visible:ring-[#075B3A]" />
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-[#5F6B66] py-3 border-y border-[#E3EAE5]">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#15935F]" /> Reliable support</span>
              <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-[#15935F]" /> Pan-India dispatch</span>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 rounded-md bg-[#25D366] hover:bg-[#1EBE5A] text-white font-bold text-base">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="inline-flex items-center gap-2"><MessageCircle className="w-5 h-5" /> Send via WhatsApp</span>}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
